import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Autocomplete, TextField, Chip } from "@mui/material";

export const AutoCompletes = ({
  // Required props
  options = [],
  value = null,
  onChange = () => {},

  // Basic props
  label = "Select Option",
  placeholder = "",
  disabled = false,
  multiple = false,
  required = false,
  error = false,
  helperText = "",

  // Size and style
  size = "small", // "small" | "medium"
  fullWidth = true,

  // Data structure (for object options)
  labelKey = "label", // which key to use for display text
  valueKey = "value", // which key to use for actual value

  // Advanced
  freeSolo = false, // allow custom input
  noOptionsText = "No options",
  loading = false,
  limitTags = -1, // limit number of tags shown when multiple=true (-1 for no limit)

  // Other props
  id,
  className = "",
  isWidth,
  ...otherProps
}) => {
  // Convert value to display format
  const getDisplayValue = () => {
    if (multiple) {
      if (!Array.isArray(value)) return [];
      return value.map((val) => findOptionByValue(val)).filter(Boolean);
    } else {
      if (!value) return null;
      return findOptionByValue(value);
    }
  };

  // Find option by value
  const findOptionByValue = (searchValue) => {
    if (!searchValue) return null;

    return (
      options.find((option) => {
        if (typeof option === "string") {
          return option === searchValue;
        }
        if (typeof option === "object") {
          return (
            option[valueKey] === searchValue || option[labelKey] === searchValue
          );
        }
        return option === searchValue;
      }) || searchValue
    );
  };

  // Get label for display
  const getOptionLabel = (option) => {
    if (!option) return "";
    if (typeof option === "string") return option;
    if (typeof option === "object") {
      return option[labelKey] || option[valueKey] || String(option);
    }
    return String(option);
  };

  // Get value for onChange
  const getOptionValue = (option) => {
    if (!option) return null;
    if (typeof option === "string") return option;
    if (typeof option === "object") {
      return option[valueKey] !== undefined
        ? option[valueKey]
        : option[labelKey];
    }
    return option;
  };

  // Handle selection change
  const handleChange = (event, newValue) => {
    if (multiple) {
      const values = Array.isArray(newValue)
        ? newValue.map(getOptionValue)
        : [];
      onChange(values);
    } else {
      const singleValue = getOptionValue(newValue);
      onChange(singleValue);
    }
  };

  // Check if options are equal
  const isOptionEqualToValue = (option, value) => {
    if (!option || !value) return option === value;

    const optionVal = getOptionValue(option);
    const valueVal = getOptionValue(value);

    return optionVal === valueVal;
  };

  return (
    <Autocomplete
      // sx={{
      //   minWidth: isWidth ? 223 : 0,
      // }}
      id={id}
      size={size}
      fullWidth={fullWidth}
      multiple={multiple}
      freeSolo={freeSolo}
      disabled={disabled}
      loading={loading}
      options={options}
      value={getDisplayValue()}
      onChange={handleChange}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      noOptionsText={noOptionsText}
      {...(multiple && { limitTags })}
      className={className}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => (
          <Chip
            label={getOptionLabel(option)}
            {...getTagProps({ index })}
            key={getOptionValue(option) || index}
            size={size}
          />
        ))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          required={required}
          error={error}
          helperText={helperText}
          variant="outlined"
        />
      )}
      {...otherProps}
    />
  );
};

export const TextFields = ({
  id,
  label,
  value,
  onChange,
  placeholder,
  className = "",
  disabled,
  multiline = false,
  minRows = 1,
  maxRows = 10,
  error = false,
  helperText = "",
}) => {
  return (
    <TextField
      id={id}
      label={label}
      variant="outlined"
      size="small"
      disabled={disabled}
      multiline={multiline}
      minRows={multiline ? minRows : undefined}
      maxRows={multiline ? maxRows : undefined}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      error={error}
      helperText={helperText}
      inputProps={{
        autoComplete: "off",
      }}
      className={`w-full ${className} px-2`}
      sx={{
        "& .MuiOutlinedInput-root": {
          fontSize: "1rem",
          backgroundColor: "#fff",
        },
        "& .MuiInputLabel-root": {
          fontSize: "1rem",
        },
      }}
    />
  );
};

export const DateInput = ({
  id,
  label = "Select Date",
  value,
  onChange,
  onKeyDown, // 👈 accept prop
  className = "",
  disabled = false,
  maxDate,
  minDate,
}) => {
  return (
    <div className={`w-full ${className}`}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <DatePicker
          label={label}
          value={value ? new Date(value) : null}
          format="dd/MM/yyyy"
          onChange={onChange}
          disabled={disabled}
          maxDate={maxDate}
          minDate={minDate}
          slotProps={{
            textField: {
              id,
              size: "small",
              fullWidth: true,
              autoComplete: "off",
              className: "Listinputcss",
             inputProps: {
                onKeyDown, // ✅ THIS is the real hook
              },
            },
          }}
        />
      </LocalizationProvider>
    </div>
  );
};
